import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, forkJoin, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, map, scan, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { environment as env } from '../../../environments/environment';
import { FILTER_NO_CERTIFICATE } from '../../components/filter/filter.constants';
import { Filter } from '../../models/filter.model';
import { Package } from '../../models/package.model';
import { Product, ProductObservable } from '../../models/product.model';
import { FilterService } from '../../services/filter.service';
import { ProductApiService } from '../../services/product-api.service';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductsComponent implements OnInit, OnDestroy {

  private subscriptions = new Map<string, Subscription>();
  $expandAllToggle = new ReplaySubject<boolean>(1);
  expandAll$ = this.$expandAllToggle.pipe(
    startWith(false),
    scan(expand => !expand),
    distinctUntilChanged(),
    shareReplay(1),
    delay(10), // stagger updates to avoid freezing the whole GUI
  );

  $filter = new Subject<Filter>();
  products$: Observable<ProductObservable[]>;
  private subjects: Record<string, ReplaySubject<Product>> = {};
  private observables: Record<string, Observable<Product>> = {};

  public packetLengths = env.packetLengths;

  constructor(
    private router: Router,
    public productApiService: ProductApiService,
    private filterService: FilterService,
  ) { }

  ngOnInit() {

    // these are the conditions a package must match to show up in free-text search
    const tests: Array<(pkg: Package, lcq: string, isNumber: boolean, qNumber: number) => boolean> = [
      (pkg, queryAsLowerCase, queryIsNumber) => queryIsNumber && pkg.number?.includes(queryAsLowerCase),
      (pkg, queryAsLowerCase, queryIsNumber, queryAsNumber) => queryIsNumber && pkg.lengthDistribution?.lengths?.includes(queryAsNumber),
      (pkg, queryAsLowerCase) => pkg.warehouseCode?.toLocaleLowerCase()?.includes(queryAsLowerCase),
      (pkg, queryAsLowerCase) => pkg.warehouseName?.toLocaleLowerCase()?.includes(queryAsLowerCase),
      (pkg, queryAsLowerCase) => pkg.certificateCode?.toLocaleLowerCase()?.includes(queryAsLowerCase),
      (pkg, queryAsLowerCase) => pkg.certificateName?.toLocaleLowerCase()?.includes(queryAsLowerCase),
      (pkg, queryAsLowerCase) => pkg.shippingMark?.toLocaleLowerCase()?.includes(queryAsLowerCase),
      (pkg, queryAsLowerCase) => pkg.statusCode?.toLocaleLowerCase()?.includes(queryAsLowerCase),
    ];

    // products
    this.products$ = combineLatest([
      this.productApiService.products.pipe(debounceTime(50)),
      this.filterService.filter$.pipe(startWith({} as Filter)),
    ])
      .pipe(
        debounceTime(25),
        tap(([products]) => {
          // setup subjects for each product, so we can keep an observable list of the packages for each product
          const codes = products.map(product => product.code);
          // clean up no longer required subjects
          const unusedSubjects = Object.keys(this.subjects).filter(code => !codes.includes(code));
          unusedSubjects.forEach(code => {
            this.subjects[code].complete();
            delete this.subjects[code];
            delete this.observables[code];
          });
          products.forEach(product => {
            this.subjects[product.code] = this.subjects[product.code] || new ReplaySubject<Product>(1);
            this.observables[product.code] = this.subjects[product.code].asObservable(); // don't debounce this. it will mess with the trackby function
          });
        }),
        map(([products, filter]) => {

          const queryNumber = parseInt(filter.query, 10);
          const queryIsANumber = !Number.isNaN(queryNumber);

          return products
            .map((product: Product) => {

              const productNameInLowerCase = product?.name.toLocaleLowerCase() || '';
              const productNameMatchesQuery = filter.query && productNameInLowerCase.includes(filter.queryLowerCase);

              let packages = product.packages;

              // free text search
              if (filter.query && !productNameMatchesQuery) {
                const packageIds = new Set();
                packages.forEach(pkg => {
                  if (tests.some(fn => fn(pkg, filter.queryLowerCase, queryIsANumber, queryNumber))) {
                    packageIds.add(pkg.number);
                  }
                });
                packages = packages.filter(pkg => packageIds.has(pkg.number));
              }

              // filter on warehouse
              if (filter.warehouse) {
                packages = packages.filter(pack =>
                  pack.warehouseCode === filter.warehouse
                  || pack.warehouseName?.toLocaleLowerCase().includes(filter.queryLowerCase)
                  || pack.warehouseCode?.toLocaleLowerCase().includes(filter.queryLowerCase)
                );
              }

              // filter on certificate
              if (filter.certificate) {
                packages = packages.filter(pack => filter.certificate === FILTER_NO_CERTIFICATE ? !pack.certificateCode : pack.certificateCode === filter.certificate);
              }

              // filter on landed status
              // 2020-09-02: customer wanted to reverted this filter to the old behavior
              // const allowedStatusCodes = [filter.showLanded && 'FREE', filter.showNotLanded && 'VESS'].filter(x => !!x);
              // packages = packages.filter(pack => allowedStatusCodes.includes(pack.statusCode));

              if (filter.showLanded) {
                packages = packages.filter(pack => pack.statusCode === 'FREE');
              }
              
              if (filter.showNotLanded) {
                packages = packages.filter(pack => pack.statusCode === 'VESS');
              }

              // filter on single length
              if (filter.showSingleLength) {
                packages = packages.filter(pack => pack.lengthDistribution.minimum === pack.lengthDistribution.maximum);
              }

              // filter on min and max length
              if (filter.minLength && filter.maxLength) {
                packages = packages.filter(pkg => pkg.lengthDistribution.maximum >= filter.minLength && pkg.lengthDistribution.minimum <= filter.maxLength);
              } else if (filter.minLength) {
                packages = packages.filter(pkg => pkg.lengthDistribution.maximum >= filter.minLength);
              } else if (filter.maxLength) {
                packages = packages.filter(pkg => pkg.lengthDistribution.minimum <= filter.maxLength);
              }

              product.totalVolume = packages.reduce((volume, p) => volume += p.volume.value, 0);
              return Object.assign({}, { ...product }, { packages });
            })
            // skip empty products. shouldn't be any by default but filters can create some.
            .filter((item: Product) => item.packages.length)
            // sort remaining products by name
            .sort((a: Product, b: Product) => a.name < b.name ? -1 : (a.name > b.name ? 1 : 0));
        }),
        tap(products => {
          // update each project subject with the current packages
          products.forEach(product => {
            // console.log('product.code', product.code, );
            this.subjects[product.code].next(product);
          });
        }),
        map(products => products.map(product => ({ code: product.code, observable: this.observables[product.code] } as ProductObservable))),
      );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onToggleUnavailableOnly() {
    // this.filterLanded = !this.filterLanded;
    // this.filterUnavailableOnly.next(this.filterLanded);
  }

  filterChanged(filter: Filter) {
    this.$filter.next(filter);
  }

  goBack() {
    this.router.navigate(['/terminals']);
  }

  getFinalizedResult() {
    const headers = ['Product Code', 'Product Name', 'Package No', 'Warehouse', 'Cert', 'Landed', 'S.Mark', 'M3', 'kg', 'Tot Pcs', ...env.packetLengths.map(length => length.toString())];
    this.products$
      .pipe(
        take(1),
        map((productObservables: ProductObservable[]) => productObservables.map((productObservable) => productObservable.observable.pipe(take(1)))),
        switchMap(productObservables => forkJoin(productObservables))
      )
      .subscribe((products: Product[]) => {
        const productsData = products.flatMap(product => this.mapProperties(product))
        const csvContent = `data:text/csv;charset=utf-8,${headers.join(',')}\n${productsData.join('\n')}`;
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', 'products.csv');
        document.body.appendChild(link);
        link.click();
      })
  }

  mapProperties(product) {
    return product.packages.map(pack => {
      const { code, name } = product;
      const { number, warehouseCode, certificateName, arrivalDate, shippingMark, volume, netWeight, pieces, lengthDistribution } = pack;
      const modulePieces = {};
      env.packetLengths.forEach(key => {
        modulePieces[key] = lengthDistribution.modulePieces.find(item => item.length === key)?.value ?? '0';
      });
      return [code, name, number, warehouseCode, certificateName, new Date(arrivalDate).toLocaleDateString(), shippingMark, volume.value, netWeight.value, pieces.value, ...Object.values(modulePieces)];
    });
  }

}
